import type { ChangeEventHandler, FC } from 'react'

import { useReactiveVar } from '@apollo/client'
import { getConfig } from '@shared/config'
import { I18nLang } from '@shared/domain/i18n'
import { localizationVar } from 'lib/apollo/reactive-vars'
import { useEffect } from 'react'
import styled from 'styled-components'

const { domain } = getConfig()

const DEFAULT_LOCALE = domain.defaultLocale

interface ClassName {
  className?: string
}

const isI18nLang = (value: string): value is I18nLang =>
  !!Object.values(I18nLang).includes(value as I18nLang)

const handleChange: ChangeEventHandler<HTMLInputElement> = (event) => {
  if (isI18nLang(event.target.value)) {
    localizationVar(event.target.value)
  }
}

const _Toggle: FC<ClassName & { lang: I18nLang }> = ({ className, lang }) => {
  const currentLang = useReactiveVar(localizationVar)

  return (
    <label className={className}>
      <input
        data-test-id={`${lang}-lang-selector`}
        defaultChecked={currentLang === lang}
        name="lang-selector"
        onChange={handleChange}
        type="radio"
        value={lang}
      />
      <span>{lang}</span>
    </label>
  )
}

const Toggle = styled(_Toggle)(({ theme }) => ({
  display: 'flex',

  input: {
    display: 'none',
  },

  span: {
    ...theme.variants.body2.medium,
    boxShadow: `inset 0 0 0 ${theme.color.navigationHighlight}`,
    cursor: 'pointer',
    padding: theme.spacings.small,
    textTransform: 'uppercase',
    transition: 'box-shadow 125ms ease-in',
  },

  'input:checked + span': {
    boxShadow: `inset 0 -3px 0 ${theme.color.navigationHighlight}`,
    color: theme.color.navigationHighlight,
  },
}))

/**
 * Language is updated throughout the app with localizationVar and persisted in sessionStorage.
 * Language can be switched at any time with the buttons in this component.
 */
const _LanguageSelector: FC<ClassName> = ({ className }) => {
  const language = useReactiveVar(localizationVar)

  useEffect(() => {
    const localizationFromSession = sessionStorage?.getItem('localization')
    if (!localizationFromSession && !language) {
      sessionStorage?.setItem('localization', DEFAULT_LOCALE)
      localizationVar(DEFAULT_LOCALE)
      return
    }
    if (language) {
      sessionStorage?.setItem('localization', language.toString())
      return
    }
    if (localizationFromSession) {
      localizationVar(
        localizationFromSession === I18nLang.SV.toString() ? I18nLang.SV : I18nLang.FI,
      )
    }
  }, [language])

  return (
    <form className={className}>
      <fieldset>
        <Toggle lang={I18nLang.FI} />
        <Toggle lang={I18nLang.SV} />
      </fieldset>
    </form>
  )
}

_LanguageSelector.displayName = 'LanguageSelector'

export const LanguageSelector = styled(_LanguageSelector)({
  display: 'flex',

  fieldset: {
    display: 'flex',
    border: 'none',
  },
})
